import React, { useEffect, useState } from 'react';
import { Button, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { IAgencyDashFilter } from '../dto/IAgencyDashFilter';
import { AgencyService } from '../../service';
import { ErrorToast } from '../../../../utils/toasters';
import { ICompanyUser } from '../../../shared/user/dto/ICompanyUser';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { UserRoles } from '../../../../enum/UserRoles';
import { SearchInput } from '../../../../shared/components/SearchInput';

interface IAgencyFilterProps {
    filterParams: IAgencyDashFilter;
    setFilterParams: React.Dispatch<React.SetStateAction<IAgencyDashFilter>>;
    isFilterLoading?: boolean;
}

type TSortCriteria = '' | 'totalFollowers' | 'createdAt' | 'totalEngagements';

export function AgencyFilter({ filterParams, setFilterParams, isFilterLoading = false }: IAgencyFilterProps) {
    const { t } = useTranslation();
    const [companyUsers, setCompanyUsers] = useState<ICompanyUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<ICompanyUser>();
    const { user } = useSelector((state: IAllStates) => state.auth);

    useEffect(() => {
        AgencyService.getAgencyCompanyUsers({ isEnabled: true })
            .then(data => {
                setCompanyUsers(data);
            })
            .catch(error => ErrorToast(error));
    }, []);

    useEffect(() => {
        setSelectedUser(companyUsers?.find(u => Number(u?.id) === Number(filterParams?.assignedManagerId)));
    }, [JSON.stringify(filterParams?.assignedManagerId)]);

    const handleSearch = (value: string) => {
        setFilterParams(prev => ({ ...prev, page: 1, displayName: value }));
    };

    const handleUsers = (paramKey: string, paramValue: string | string[] | number) => {
        setFilterParams(prev => ({ ...prev, page: 1, [paramKey]: paramValue }));
    };

    const handleSortCriteria = (sortCriteria: TSortCriteria) => {
        setFilterParams(prev => ({ ...prev, sortCriteria, page: 1 }));
    };

    const toggleSortType = () => {
        setFilterParams(prev => ({ ...prev, sortType: prev.sortType === 'DESC' ? 'ASC' : 'DESC' }));
    };

    return (
        <Row className="mb-4">
            {user.userType === UserRoles.AGENCY && (
                <Col md={4}>
                    <SearchInput
                        name="displayName"
                        defaultValue={filterParams?.displayName ?? ''}
                        onSearch={handleSearch}
                        isLoading={isFilterLoading}
                        label={t('agency.filter.name')}
                        className="w-100 mb-2 my-md-0"
                    />
                </Col>
            )}
            {user.userType === UserRoles.AGENCY_MASTER && (
                <Col md={4}>
                    <CustomAutocomplete
                        dataArray={companyUsers}
                        isWithImage={true}
                        isMultiple={false}
                        inputLabel="firstName"
                        queryFilterKey="assignedManagerId"
                        photoLabel="profilePhoto.path"
                        selectedStateImage={selectedUser?.profilePhoto?.path as string}
                        inputLabelTranslation={t('agency.filter.managing')}
                        handleChangeParams={handleUsers}
                        isDisabled={isFilterLoading}
                        me={true}
                    />
                </Col>
            )}
            <Col md={8} className="pr-0">
                <div className="d-flex align-items-center justify-content-end">
                    <Button onClick={toggleSortType} variant="transparent" aria-label="Sort type" className="px-0">
                        {filterParams.sortType === 'ASC' ? (
                            <BsSortUp style={{ height: 16, width: 16 }} />
                        ) : (
                            <BsSortDown style={{ height: 16, width: 16 }} />
                        )}
                    </Button>
                    <DropdownButton
                        className="mr-2 pl-1 font-weight-normal"
                        variant="transparent"
                        title={
                            <span>
                                {t(
                                    !filterParams?.sortCriteria
                                        ? 'general.button.sort_by'
                                        : `brand.campaign.book.offer.filters.${filterParams?.sortCriteria}`
                                )}
                            </span>
                        }
                    >
                        {filterParams?.sortCriteria && (
                            <Dropdown.Item onClick={() => handleSortCriteria('')}>
                                {t('agency.filter.clear')}
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={() => handleSortCriteria('totalFollowers')}>
                            {t('agency.filter.totalFollowers')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleSortCriteria('createdAt')}>
                            {t('agency.filter.createdAt')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleSortCriteria('totalEngagements')}>
                            {t('agency.filter.totalEngagements')}
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </Col>
        </Row>
    );
}
